/**
 * The first real navigation in the product.
 *
 * ┌─ ⛔ NO ROUTER LIBRARY, AND THE REASON IS NOT SIZE ──────────────────────────┐
 * │ A router brings its own model of what a route IS - matchers, loaders,       │
 * │ nested outlets, its own history abstraction - and the product needs exactly │
 * │ one thing: a conversation id in the URL. **A dependency here is the third   │
 * │ environment problem in miniature**: a second set of concepts to learn, to   │
 * │ test against, and to reason about when something behaves oddly.             │
 * │                                                                            │
 * │ What follows is `popstate` plus `history.pushState`, and the PARSING is a   │
 * │ pure function - so the decisions are checkable in the server project        │
 * │ without a DOM, exactly like `thread-view.ts` and `connection-presentation`. │
 * └────────────────────────────────────────────────────────────────────────────┘
 */

/**
 * ⛔ THE PARSING LIVES IN `route-path.ts` AND IS RE-EXPORTED HERE.
 *
 * The header above claimed the parsing was "checkable in the server project
 * without a DOM" while this module touched `window`, so importing it from that
 * project failed to typecheck. The claim is now true rather than aspirational -
 * see `route-path.ts` for how it was found. Callers import from here as before.
 */
export { type Route, parseRoute, routePath } from './route-path.js'
// ⚠️ IMPORTED AS WELL AS RE-EXPORTED. `export ... from` forwards the names to
// consumers without binding them in this module's own scope, and `currentRoute`
// below calls `parseRoute` directly.
import { type Route, parseRoute, routePath } from './route-path.js'

/**
 * ⛔ `pushState`, NOT `replaceState`, AND THE REASON ONLY SURFACES IN USE.
 *
 * An agent triaging ten conversations opens each in turn. With `push`, back
 * walks them - which is what a person expects and what makes the browser's own
 * control useful. With `replace`, back skips the ENTIRE triage session and
 * leaves wherever they were before the inbox.
 *
 * ⚠️ **THAT IS INVISIBLE UNTIL SOMEBODY TRIES IT**, and it is far easier to
 * decide now than to change once anyone has built a habit on either behaviour.
 */
/**
 * ┌─ ⛔ THE UNSAVED-CHANGES GUARD. Amir 2026-09-12: edits need Save and Cancel, ┐
 * │   and leaving with unsaved changes prompts.                                 │
 * │                                                                            │
 * │ A screen registers a predicate. Every in-app navigation asks it first, and  │
 * │ a `false` abandons the navigation. The predicate is what PROMPTS - this     │
 * │ module has no opinion about how - because the screen knows what is unsaved  │
 * │ and this does not.                                                         │
 * └────────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ ONE GUARD, NOT A LIST, and that is a real limitation rather than a
 * simplification: two screens with unsaved edits cannot both be mounted in this
 * product, because a route renders one. A list would be a mechanism with no
 * second caller and no way to test the interesting case.
 */
/**
 * ⛔ THE GUARD IS HANDED A `retry`, AND THAT IS WHAT MAKES A MODAL POSSIBLE.
 * Amir's finding 7: the prompt must be the platform's, not `window.confirm`.
 *
 * ⚠️ `window.confirm` IS SYNCHRONOUS AND A MODAL IS NOT, so a predicate
 * returning true or false cannot express "ask, and continue if they say yes".
 * The guard now **blocks by returning false and keeps the `retry`**; the screen
 * shows its dialog and calls `retry()` on confirm, which re-runs the exact
 * navigation with the guard suspended for that one call.
 */
let guard: ((retry: () => void) => boolean) | null = null

export function setNavigationGuard(fn: ((retry: () => void) => boolean) | null): void {
  guard = fn
}

/**
 * ⚠️ SET ONLY FOR THE DURATION OF A `retry`, so the guard does not refuse the
 * navigation it just approved. A flag the screen had to clear itself would be a
 * flag somebody forgets to clear, and the screen would then be unleavable.
 */
let bypass = false

/**
 * ⚠️ SET WHILE `navigate` DISPATCHES ITS OWN `popstate`, SO THE GUARD IS ASKED
 * ONCE PER NAVIGATION. `navigate` has to fire the event itself because the
 * browser does not fire it for `pushState`, and the listener below cannot tell
 * that event from a real Back - so it would prompt a second time for a
 * navigation the person had already confirmed.
 */
let navigating = false

function mayLeave(retry: () => void): boolean {
  if (guard === null || bypass) return true
  return guard(retry)
}

export function navigate(route: Route, opts: { replace?: boolean } = {}): void {
  const path = routePath(route)
  /**
   * ⛔ COMPARED AGAINST PATHNAME **AND SEARCH**, which was the second half of
   * the search bug. Against `pathname` alone, standing at `/search?q=hello` and
   * clearing the box asks for `/search` - which equalled `pathname`, returned
   * early, and left a query in the URL the field no longer had.
   */
  if (path === window.location.pathname + window.location.search) return
  /** ⛔ Asked before the URL moves, or the guard would be deciding whether to
   *  undo a navigation that has already happened. */
  if (
    !mayLeave(() => {
      bypass = true
      try {
        navigate(route, opts)
      } finally {
        bypass = false
      }
    })
  ) {
    return
  }
  /**
   * ⛔ `replace` EXISTS FOR THE `/` REDIRECT AND FOR NOTHING ELSE YET.
   *
   * `/` is not a route, so `App` sends it to `/inbox` on mount. Pushing that
   * would put a path in history whose only behaviour is to redirect forward
   * again - so Back from the inbox would land on `/`, immediately bounce, and
   * read as a broken Back button rather than as a redirect.
   *
   * ⚠️ EVERY OTHER CALLER STILL PUSHES, deliberately: opening a conversation and
   * running a search are navigations a person made and expects Back to undo.
   */
  if (opts.replace === true) window.history.replaceState(null, '', path)
  else window.history.pushState(null, '', path)
  navigating = true
  try {
    window.dispatchEvent(new PopStateEvent('popstate'))
  } finally {
    navigating = false
  }
}

/**
 * Subscribe to route changes. Returns an unsubscribe, like `subscribe()` in the
 * query store - and for the same reason: a listener nobody releases outlives the
 * component that added it.
 *
 * ⚠️ `navigate` DISPATCHES `popstate` ITSELF because the browser does not fire
 * it for `pushState`. Without that, a programmatic navigation updates the URL
 * and nothing re-renders - which looks like a broken link rather than a missing
 * event.
 */
export function onRouteChange(listener: () => void): () => void {
  /**
   * ⛔ AND BACK IS GUARDED TOO, WHICH TAKES MORE THAN A RETURN. `popstate`
   * cannot be cancelled: by the time it fires the URL has already moved. So a
   * refused Back is undone by pushing the path the person was on.
   *
   * ⚠️ THE COST IS STATED RATHER THAN HIDDEN: this leaves the history stack one
   * entry longer than it was, because there is no way to un-pop. `history.go(1)`
   * would restore the stack exactly and is asynchronous, which means the app
   * renders the wrong route for a frame and the guard can re-enter. **A tidy
   * stack is worth less than not losing somebody's edits.**
   */
  let lastPath = window.location.pathname + window.location.search
  const handler = () => {
    const next = window.location.pathname + window.location.search
    /**
     * ⚠️ THE RETRY FOR A REFUSED BACK IS ANOTHER BACK, not a navigation to the
     * path the person was heading for: we have already pushed them forward
     * again, so the stack is one entry longer and `back()` is what undoes both.
     * `bypass` is cleared by the handler that then runs, which is why it is set
     * here and released below rather than in a `finally` around an async call.
     */
    if (!navigating && next !== lastPath && !mayLeave(() => {
      bypass = true
      window.history.back()
    })) {
      window.history.pushState(null, '', lastPath)
      return
    }
    bypass = false
    lastPath = next
    listener()
  }
  window.addEventListener('popstate', handler)
  return () => window.removeEventListener('popstate', handler)
}

/**
 * ⛔ `pathname` **PLUS** `search`. `pathname` alone silently drops `?q=`, which
 * is the entire state of the search page - see `parseRoute`'s header. **This one
 * line was the bug**, and it was invisible on every other route because no other
 * route carries a query.
 */
export function currentRoute(): Route {
  return parseRoute(window.location.pathname + window.location.search)
}
